/**
 * Security Configuration
 * CSP, security headers, and XSS protection settings
 */

// Content Security Policy directives
export const CSP_DIRECTIVES = {
  'default-src': ["'self'"],
  'script-src': [
    "'self'",
    "'unsafe-inline'", // Required for some React features
    "'unsafe-eval'",     // Required for some build tools
    // Add specific trusted domains as needed
    // 'https://trusted-cdn.com',
  ],
  'style-src': [
    "'self'",
    "'unsafe-inline'", // Required for styled-components/emotion
    'https://fonts.googleapis.com',
  ],
  'img-src': [
    "'self'",
    'data:',
    'blob:',
    'https:',
  ],
  'font-src': [
    "'self'",
    'https://fonts.gstatic.com',
    'data:',
  ],
  'connect-src': [
    "'self'",
    // API endpoints
    import.meta.env.VITE_API_URL || 'http://localhost:4000',
    // WebSocket for real-time features
    'ws:',
    'wss:',
  ],
  'media-src': [
    "'self'",
    'blob:',
  ],
  'object-src': ["'none'"], // Disallow Flash and other plugins
  'frame-src': [
    "'self'",
  ],
  'frame-ancestors': ["'self'"], // Prevent clickjacking
  'base-uri': ["'self'"],
  'form-action': ["'self'"],
  'upgrade-insecure-requests': [], // Upgrade HTTP to HTTPS
};

/**
 * Build CSP header string from directives
 */
export function buildCSPHeader(directives: typeof CSP_DIRECTIVES): string {
  return Object.entries(directives)
    .map(([key, values]) => {
      if (values.length === 0) return key;
      return `${key} ${values.join(' ')}`;
    })
    .join('; ');
}

/**
 * Security headers configuration
 */
export const SECURITY_HEADERS = {
  // Content Security Policy
  'Content-Security-Policy': buildCSPHeader(CSP_DIRECTIVES),

  // Prevent MIME type sniffing
  'X-Content-Type-Options': 'nosniff',

  // XSS Protection (legacy, but still useful)
  'X-XSS-Protection': '1; mode=block',

  // Frame options (clickjacking protection)
  'X-Frame-Options': 'SAMEORIGIN',

  // Referrer policy
  'Referrer-Policy': 'strict-origin-when-cross-origin',

  // Permissions policy (formerly Feature-Policy)
  'Permissions-Policy': [
    'accelerometer=()',
    'camera=()',
    'geolocation=()',
    'gyroscope=()',
    'magnetometer=()',
    'microphone=()',
    'payment=()',
    'usb=()',
  ].join(', '),

  // Strict Transport Security (HSTS) - only in production
  ...(import.meta.env.MODE === 'production' && {
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  }),
};

/**
 * Apply security headers to fetch requests
 */
export function applySecurityHeaders(headers: HeadersInit = {}): HeadersInit {
  return {
    ...headers,
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': 'application/json',
  };
}

/**
 * Sanitize user input to prevent XSS
 * Basic sanitization - use DOMPurify for HTML content
 */
export function sanitizeInput(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/**
 * Validate and sanitize URL
 */
export function sanitizeUrl(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin);

    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }

    // Prevent javascript: and data: URLs
    if (parsed.protocol === 'javascript:' || parsed.protocol === 'data:') {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Generate CSRF token
 */
export function generateCSRFToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Store CSRF token securely
 */
export function storeCSRFToken(token: string): void {
  // Store in sessionStorage (not localStorage for security)
  sessionStorage.setItem('csrf_token', token);
}

/**
 * Get stored CSRF token
 */
export function getCSRFToken(): string | null {
  return sessionStorage.getItem('csrf_token');
}

/**
 * Clear CSRF token
 */
export function clearCSRFToken(): void {
  sessionStorage.removeItem('csrf_token');
}

/**
 * Add CSRF token to request headers
 */
export function withCSRFToken(headers: HeadersInit = {}): HeadersInit {
  const token = getCSRFToken();
  if (token) {
    return {
      ...headers,
      'X-CSRF-Token': token,
    };
  }
  return headers;
}

/**
 * Security utility functions
 */
export const SecurityUtils = {
  // Check if string contains potential XSS patterns
  containsXSS: (input: string): boolean => {
    const xssPatterns = [
      /<script\b[^\u003c]*(?:(?!<\/script>)<[^\u003c]*)*<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi, // onclick, onload, etc.
      /<iframe/gi,
      /<object/gi,
      /<embed/gi,
    ];
    return xssPatterns.some((pattern) => pattern.test(input));
  },

  // Validate file upload
  validateFileUpload: (
    file: File,
    allowedTypes: string[],
    maxSize: number
  ): { valid: boolean; error?: string } => {
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Invalid file type' };
    }
    if (file.size > maxSize) {
      return { valid: false, error: 'File too large' };
    }
    return { valid: true };
  },

  // Generate secure random ID
  generateSecureId: (): string => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  },

  // Hash string (simple, not for passwords)
  hashString: async (input: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  },
};

/**
 * Initialize security features
 */
export function initSecurity(): void {
  // Generate and store CSRF token
  const token = generateCSRFToken();
  storeCSRFToken(token);

  // Add CSRF token to Axios defaults if using Axios
  if (typeof window !== 'undefined' && (window as any).axios) {
    (window as any).axios.defaults.headers.common['X-CSRF-Token'] = token;
  }

  // Log security initialization in development
  if (import.meta.env.MODE === 'development') {
    console.log('[Security] Initialized with CSRF token');
  }
}

export default {
  CSP_DIRECTIVES,
  SECURITY_HEADERS,
  buildCSPHeader,
  applySecurityHeaders,
  sanitizeInput,
  sanitizeUrl,
  generateCSRFToken,
  storeCSRFToken,
  getCSRFToken,
  clearCSRFToken,
  withCSRFToken,
  SecurityUtils,
  init: initSecurity,
};
